import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

export interface IUsuarioSimples {
  id?: number;
  nome: string;
  email: string;
  imagem_url?: string;
}

export interface ISolicitacaoAcesso {
  id: number;
  guid: string;
  solicitante_id: number;
  usuario_alvo_id: number;
  status: 'pendente' | 'aprovado' | 'rejeitado' | 'expirado';
  duracao_horas?: number; // Duração solicitada em horas
  dt_expiracao?: string;
  dt_resposta?: string;
  dt_criacao: string;
  solicitante?: IUsuarioSimples;
  usuario_alvo?: IUsuarioSimples;
  token_acesso?: string; // Apenas retornado na aprovação
}

export interface ISolicitacaoAcessoCreate {
  usuario_alvo_id: number;
  duracao_horas?: number; // Duração em horas (opcional, padrão 168 = 7 dias)
  dt_expiracao?: string; // Data de expiração específica (ISO string)
}

export const useSolicitacaoAcesso = () => {
  // Cria uma nova solicitação de acesso (admin -> usuário)
  const criarSolicitacao = async (
    data: ISolicitacaoAcessoCreate,
  ): Promise<IDataNoPagination<ISolicitacaoAcesso>> => {
    const res = await api.post<IDataNoPagination<ISolicitacaoAcesso>>(
      '/api/v1/solicitacoes-acesso/',
      data,
    );
    return res.data;
  };

  // Lista solicitações pendentes para o usuário logado (para mostrar modal)
  const getPendentes = async (): Promise<IDataNoPagination<ISolicitacaoAcesso[]>> => {
    const res = await api.get<IDataNoPagination<ISolicitacaoAcesso[]>>(
      '/api/v1/solicitacoes-acesso/pendentes/',
    );
    return res.data;
  };

  // Lista todas solicitações recebidas (todos os status) para o painel de notificações
  const getRecebidas = async (): Promise<IDataNoPagination<ISolicitacaoAcesso[]>> => {
    const res = await api.get<IDataNoPagination<ISolicitacaoAcesso[]>>(
      '/api/v1/solicitacoes-acesso/recebidas/',
    );
    return res.data;
  };

  // Lista solicitações feitas pelo admin logado
  const getMinhasSolicitacoes = async (): Promise<IDataNoPagination<ISolicitacaoAcesso[]>> => {
    const res = await api.get<IDataNoPagination<ISolicitacaoAcesso[]>>(
      '/api/v1/solicitacoes-acesso/minhas/',
    );
    return res.data;
  };

  // Verifica se existe solicitação ativa para um usuário específico
  const getSolicitacaoParaUsuario = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ISolicitacaoAcesso | null>> => {
    const usuarioId = key.queryKey[1] as number;
    const res = await api.get<IDataNoPagination<ISolicitacaoAcesso | null>>(
      `/api/v1/solicitacoes-acesso/usuario/${usuarioId}/`,
    );
    return res.data;
  };

  // Aprova uma solicitação (usuário alvo aprova)
  const aprovarSolicitacao = async (
    solicitacaoId: number,
  ): Promise<IDataNoPagination<ISolicitacaoAcesso>> => {
    const res = await api.put<IDataNoPagination<ISolicitacaoAcesso>>(
      `/api/v1/solicitacoes-acesso/${solicitacaoId}/aprovar/`,
    );
    return res.data;
  };

  // Rejeita uma solicitação (usuário alvo rejeita)
  const rejeitarSolicitacao = async (
    solicitacaoId: number,
  ): Promise<IDataNoPagination<ISolicitacaoAcesso>> => {
    const res = await api.put<IDataNoPagination<ISolicitacaoAcesso>>(
      `/api/v1/solicitacoes-acesso/${solicitacaoId}/rejeitar/`,
    );
    return res.data;
  };

  // Cancela uma solicitação (admin cancela)
  const cancelarSolicitacao = async (
    solicitacaoId: number,
  ): Promise<IDataNoPagination<ISolicitacaoAcesso>> => {
    const res = await api.delete<IDataNoPagination<ISolicitacaoAcesso>>(
      `/api/v1/solicitacoes-acesso/${solicitacaoId}/`,
    );
    return res.data;
  };

  // Login com acesso delegado (admin usa token para acessar conta de usuário)
  const loginAcessoDelegado = async (data: {
    email: string;
    token_acesso: string;
  }): Promise<any> => {
    const res = await api.post('/api/v1/login/acesso-delegado/', data);
    return res.data;
  };

  return {
    criarSolicitacao,
    getPendentes,
    getRecebidas,
    getMinhasSolicitacoes,
    getSolicitacaoParaUsuario,
    aprovarSolicitacao,
    rejeitarSolicitacao,
    cancelarSolicitacao,
    loginAcessoDelegado,
  };
};
